import type * as React from "react"

import { cn } from "@/lib/utils"

import {
  FINDING_TONE,
  OUTCOME_LABEL,
  OUTCOME_TONE,
  type EvalFinding,
  type EvalMark,
  type PointBox,
} from "../eval-runs-data"

import type { PageGeometry } from "./pdf-page"
import { anchorOf, type Selection } from "./selection"

/** Width of the remark card that opens beside a selected box, in CSS pixels. */
const CARD_WIDTH = 300

export interface OverlayProps {
  page: number
  geometry: PageGeometry
  marks: readonly EvalMark[]
  findings: readonly EvalFinding[]
  selection: Selection
  onSelect: (s: Selection) => void
  remarkCounts: ReadonlyMap<string, number>
  card: React.ReactNode
}

function tint(tone: string, percent: number): string {
  return `color-mix(in srgb, ${tone} ${String(percent)}%, transparent)`
}

/** The boxes on one page, their remark counts, and the remark card beside a selected one. */
export function PageOverlays(props: OverlayProps) {
  const { geometry, page, selection } = props
  const sx = geometry.width / geometry.pointWidth
  const sy = geometry.height / geometry.pointHeight
  const place = (b: PointBox) => ({
    left: b[0] * sx,
    top: b[1] * sy,
    width: Math.max(4, (b[2] - b[0]) * sx),
    height: Math.max(4, (b[3] - b[1]) * sy),
  })
  const marks = props.marks.filter((m) => m.page === page && m.rect)
  const findingBoxes = props.findings.flatMap((f) =>
    f.boxes.filter((b) => b.page === page).map((b) => ({ finding: f, bbox: b.bbox })),
  )
  const selectedMarkId = selection?.kind === "mark" ? selection.id : undefined
  const selectedMark = props.marks.find((m) => m.id === selectedMarkId)
  const selectedFindingId =
    selection?.kind === "finding" ? selection.id : (selectedMark?.findingId ?? undefined)

  const anchor = anchorOf(selection, props.marks, props.findings)
  const cardStyle = (() => {
    if (anchor?.page !== page || !props.card) return undefined
    const box = place(anchor.box)
    const right = box.left + box.width + 8
    const left =
      right + CARD_WIDTH <= geometry.width ? right : Math.max(0, box.left - CARD_WIDTH - 8)
    const top = Math.min(Math.max(0, box.top), Math.max(0, geometry.height - 340))
    return { left, top, width: CARD_WIDTH }
  })()

  const count = (key: string) => {
    const n = props.remarkCounts.get(key)
    return n ? (
      <span
        className="absolute -top-2 -right-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-white"
        aria-label={`${String(n)} remark${n === 1 ? "" : "s"}`}
      >
        {n}
      </span>
    ) : null
  }

  return (
    <>
      {findingBoxes.map(({ finding, bbox }, i) => (
        <button
          type="button"
          key={`f${String(finding.id)}-${String(i)}`}
          aria-label={`Our finding ${String(finding.id)}: ${finding.message}`}
          title={`${finding.ruleId}: ${finding.message}`}
          onClick={() => {
            props.onSelect({ kind: "finding", id: finding.id })
          }}
          className="absolute rounded-sm border-2 border-dashed"
          style={{
            ...place(bbox),
            borderColor: FINDING_TONE,
            background: selectedFindingId === finding.id ? tint(FINDING_TONE, 22) : "transparent",
          }}
        >
          {findingBoxes.findIndex((b) => b.finding.id === finding.id) === i
            ? count(`finding:${String(finding.id)}`)
            : null}
        </button>
      ))}
      {marks.map((m) =>
        m.rect ? (
          <button
            type="button"
            key={m.id}
            aria-label={`Expert mark ${m.id}, ${OUTCOME_LABEL[m.outcome]}: ${m.comment}`}
            title={`${m.id} · ${OUTCOME_LABEL[m.outcome]}: ${m.comment}`}
            onClick={() => {
              props.onSelect({ kind: "mark", id: m.id })
            }}
            className={cn("absolute rounded-sm border-2", selectedMarkId === m.id && "z-10")}
            style={{
              ...place(m.rect),
              borderColor: OUTCOME_TONE[m.outcome],
              background: tint(OUTCOME_TONE[m.outcome], selectedMarkId === m.id ? 28 : 12),
              boxShadow:
                selectedMarkId === m.id
                  ? `0 0 0 4px ${tint(OUTCOME_TONE[m.outcome], 28)}`
                  : undefined,
            }}
          >
            {count(`mark:${m.id}`)}
          </button>
        ) : null,
      )}
      {cardStyle && (
        <div className="absolute z-20 cursor-auto" style={cardStyle}>
          {props.card}
        </div>
      )}
    </>
  )
}
